import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CopyButton } from "@/components/ui/copy-button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { formatDate } from "@/lib/format";
import { formatProblem } from "@/lib/problemDetails";

export type DemoPreset = NonNullable<components["schemas"]["DemoPreset"]>;
type DemoLink = components["schemas"]["DemoLinkAdminResponse"];
type DemoClient = { id: string; name: string };

// The server's default Demo client; preselected so the picker shows where a demo lands when left alone.
const DEFAULT_DEMO_CLIENT = "Dolfin Demo";

export const PRESET_LABELS: Record<DemoPreset, string> = {
  Cafe: "Café",
  Agency: "Agency",
  Ecommerce: "Ecommerce",
  Wholesale: "Wholesale",
};

const PRESETS = Object.keys(PRESET_LABELS) as DemoPreset[];

/** Creates a demo for a prospect (spec "AD - Demo Organisations", AC-1) and shows its magic link. */
export function NewDemoDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [prospectName, setProspectName] = useState("");
  const [prospectEmail, setProspectEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [preset, setPreset] = useState<DemoPreset>("Cafe");
  const [sendEmail, setSendEmail] = useState(true);
  const [demoClients, setDemoClients] = useState<DemoClient[]>([]);
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DemoLink | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // A failed list leaves the picker out: the server then uses its default Demo client.
    void api.GET("/admin/clients", { params: { query: { Type: "Demo", Limit: 100 } } }).then(({ data }) => {
      if (cancelled || !data) return;
      const list = (data.data ?? []).flatMap((c) => (c.id ? [{ id: c.id, name: c.name }] : []));
      setDemoClients(list);
      setClientId((c) => c ?? list.find((cl) => cl.name === DEFAULT_DEMO_CLIENT)?.id);
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  function handleOpenChange(next: boolean) {
    if (!next) {
      setProspectName("");
      setProspectEmail("");
      setCompanyName("");
      setPreset("Cafe");
      setSendEmail(true);
      setClientId(undefined);
      setError(null);
      setResult(null);
    }
    onOpenChange(next);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { data, error: apiError } = await api.POST("/admin/demos", {
      body: {
        prospectName: prospectName.trim(),
        prospectEmail: prospectEmail.trim(),
        companyName: companyName.trim(),
        preset,
        sendEmail,
        clientId: clientId ?? null,
      },
    });
    setSubmitting(false);
    if (apiError || !data) {
      const p = formatProblem(apiError, "Couldn't create the demo.");
      setError(p.detail ?? p.title);
      return;
    }
    setResult(data);
    onCreated();
    toast.success(data.emailSent ? `Demo created and emailed to ${prospectEmail.trim()}.` : "Demo created.");
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {result ? (
          <DemoLinkResult link={result} email={prospectEmail.trim()} title="Demo created" />
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle>New demo</DialogTitle>
              <DialogDescription>
                A demo organisation with a made-up business. The prospect signs in with a magic link and builds the books in
                one click.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="demo-prospect-name">Prospect name</Label>
                  <Input
                    id="demo-prospect-name"
                    value={prospectName}
                    onChange={(e) => setProspectName(e.target.value)}
                    autoFocus
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="demo-prospect-email">Prospect email</Label>
                  <Input
                    id="demo-prospect-email"
                    type="email"
                    value={prospectEmail}
                    onChange={(e) => setProspectEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-[1fr_11rem]">
                <div className="space-y-2">
                  <Label htmlFor="demo-company">Company</Label>
                  <Input
                    id="demo-company"
                    placeholder="e.g. Bean & Leaf"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="demo-preset">Business</Label>
                  <Select value={preset} onValueChange={(v) => setPreset(v as DemoPreset)}>
                    <SelectTrigger id="demo-preset" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRESETS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {PRESET_LABELS[p]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {demoClients.length > 0 ? (
                <div className="space-y-2">
                  <Label htmlFor="demo-client">Client</Label>
                  <Select value={clientId ?? ""} onValueChange={setClientId}>
                    <SelectTrigger id="demo-client" className="w-full">
                      <SelectValue placeholder={`${DEFAULT_DEMO_CLIENT} (default)`} />
                    </SelectTrigger>
                    <SelectContent>
                      {demoClients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(v === true)} className="mt-0.5" />
                <span>
                  Email the prospect
                  <span className="block text-xs text-muted-foreground">
                    Sends "Try Dolfin Books" with the magic link. Untick to copy the link yourself.
                  </span>
                </span>
              </label>
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Spinner className="size-4" /> : null}
                Create demo
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** A freshly minted magic link with its copy button: after creating a demo, or after minting a new link. */
export function DemoLinkResult({ link, email, title }: { link: DemoLink; email?: string; title: string }) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {link.emailSent
            ? `Emailed to ${email || "the prospect"}. The link also works pasted.`
            : "Not emailed. Send this link to the prospect yourself."}{" "}
          It works once{link.expiresAt ? ` and the demo runs until ${formatDate(link.expiresAt)}` : ""}.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-1.5 text-sm">
        <span className="text-muted-foreground">Magic link</span>
        <div className="flex items-center gap-2 rounded-md bg-muted p-2">
          <code className="flex-1 text-xs break-all">{link.magicLinkUrl}</code>
          <CopyButton value={link.magicLinkUrl} />
        </div>
      </div>
      <DialogFooter showCloseButton />
    </>
  );
}
